import { useState, useEffect } from 'react'
import apiClient from '../../services/apiClient'
import Layout from '../../components/Layout'
import AdminTabs from '../../components/AdminTabs'
import Spinner from '../../components/Spinner'
import ConfirmModal from '../../components/ConfirmModal'

export default function AdminUsers() {
  const [users, setUsers]     = useState([])
  const [total, setTotal]     = useState(0)
  const [teams, setTeams]     = useState([])
  const [search, setSearch]   = useState('')
  const [editId, setEditId]   = useState(null)
  const [editData, setEditData] = useState({})
  const [error, setError]     = useState(null)
  const [loading, setLoading] = useState(true)
  const [confirmDeactivate, setConfirmDeactivate] = useState(null) // user id

  const loadTeams = () => apiClient.get('/admin/teams').then((r) => setTeams(r.data.items))
  const loadUsers = () => {
    setLoading(true)
    const params = new URLSearchParams({ page_size: 100 })
    if (search) params.set('search', search)
    apiClient.get(`/admin/users?${params}`).then((r) => {
      setUsers(r.data.items)
      setTotal(r.data.total)
    }).finally(() => setLoading(false))
  }

  useEffect(() => { loadTeams() }, [])
  useEffect(() => { loadUsers() }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (u) => {
    setEditId(u.id)
    setEditData({ app_role: u.app_role, team_id: u.team_id ?? '' })
  }

  const saveEdit = async () => {
    try {
      await apiClient.put(`/admin/users/${editId}`, {
        app_role: editData.app_role,
        team_id:  editData.team_id ? Number(editData.team_id) : null,
      })
      setEditId(null)
      setError(null)
      loadUsers()
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed')
    }
  }

  const deactivate = async (id) => {
    setConfirmDeactivate(null)
    try {
      await apiClient.delete(`/admin/users/${id}`)
      loadUsers()
    } catch (e) {
      setError(e.response?.data?.detail || 'Deactivation failed')
    }
  }

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Admin</h1>
      </div>

      <AdminTabs />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{total} users</span>
        <input
          type="search"
          className="search-input"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search users"
        />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="loading-state">
          <Spinner size="md" />
          Loading users…
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Team</th>
                <th>Active</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>No users found.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.display_name}</td>
                  <td style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{u.email}</td>
                  <td>
                    {editId === u.id ? (
                      <select
                        className="form-control"
                        style={{ width: 'auto', padding: '0.25rem 0.5rem' }}
                        value={editData.app_role}
                        onChange={(e) => setEditData((d) => ({ ...d, app_role: e.target.value }))}
                      >
                        {['admin', 'user', 'viewer'].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ textTransform: 'capitalize' }}>{u.app_role}</span>
                    )}
                  </td>
                  <td>
                    {editId === u.id ? (
                      <select
                        className="form-control"
                        style={{ width: 'auto', padding: '0.25rem 0.5rem' }}
                        value={editData.team_id}
                        onChange={(e) => setEditData((d) => ({ ...d, team_id: e.target.value }))}
                      >
                        <option value="">— No team —</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    ) : (
                      u.team_name ?? '—'
                    )}
                  </td>
                  <td>
                    <span style={{ color: u.is_active ? 'var(--success-color)' : 'var(--text-secondary)' }}>
                      {u.is_active ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    {editId === u.id ? (
                      <div className="inline-edit-controls">
                        <button type="button" className="btn btn-primary btn-sm" onClick={saveEdit}>Save</button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div className="inline-edit-controls">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => startEdit(u)}>Edit</button>
                        {u.is_active && (
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirmDeactivate(u.id)}>Deactivate</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <ConfirmModal
        isOpen={confirmDeactivate !== null}
        title="Deactivate user"
        message="This user will no longer be able to log in to AI Pulse."
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onConfirm={() => deactivate(confirmDeactivate)}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </Layout>
  )
}
